import React, { createContext, useContext, useEffect, useState } from 'react'
import { 
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '../config/firebase'
import { User, RegisterForm } from '../types'

interface AuthContextType {
  user: User | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  role: 'superadmin' | 'admin' | 'agent' | 'customer' | null;
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterForm) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState<'superadmin' | 'admin' | 'agent' | 'customer' | null>(null);

  const fetchUserDocument = async (uid: string): Promise<User | null> => {
    console.log('🔍 Fetching user document for UID:', uid);
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      const userWithId = { ...userData, id: uid };
      console.log('✅ User document found:', userWithId);
      console.log('🎭 User role:', userWithId.role);
      setUser(userWithId);
      setRole(userWithId.role);
      return userWithId;
    } else {
      console.log('❌ User document not found in Firestore');
      // Auto-create user document if it doesn't exist
      try {
        console.log('🔄 Auto-creating user document...');
        const firebaseUser = auth.currentUser;
        if (firebaseUser && firebaseUser.email) {
          const newUserData = {
            email: firebaseUser.email,
            name: firebaseUser.displayName || firebaseUser.email.split('@')[0] || 'New User',
            role: 'customer' as const, // Default role
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          
          await setDoc(userDocRef, newUserData);
          console.log('✅ Auto-created user document');
          
          const userWithId = { ...newUserData, id: uid };
          setUser(userWithId);
          setRole(userWithId.role);
          return userWithId;
        }
      } catch (error) {
        console.error('❌ Error auto-creating user document:', error);
      }
    }
    return null;
  }

  useEffect(() => {
    console.log('🔄 Setting up auth state listener');
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      console.log('🔥 Firebase auth state changed:', fbUser ? 'User logged in' : 'User logged out');
      setFirebaseUser(fbUser);
      if (fbUser) {
        console.log('👤 Firebase user UID:', fbUser.uid);
        await fetchUserDocument(fbUser.uid);
      } else {
        console.log('🚪 Clearing user state');
        setUser(null);
        setFirebaseUser(null);
        setRole(null);
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email: string, password: string) => {
    console.log('🔐 Attempting login for:', email);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const firebaseUser = userCredential.user
      console.log('✅ Firebase login successful, UID:', firebaseUser.uid);

      // Explicitly fetch user data right after login to ensure role is available
      const userDoc = await fetchUserDocument(firebaseUser.uid);

      if (!userDoc) {
        console.error('❌ User document not found after login');
        // This case should be rare, but good to handle.
        // It means the user exists in Auth but not in Firestore.
        throw new Error("User data not found in database.");
      }
      console.log('🎉 Login complete, user role:', userDoc.role);
    } catch (error) {
      // Clear user state on login failure
      setUser(null);
      setFirebaseUser(null);
      setRole(null);
      console.error("❌ Login error:", error)
      throw error
    }
  }

  const register = async (data: RegisterForm) => {
    const { email, password, name, role, phone } = data;
    try {
      console.log('🔐 Starting registration process for:', email);
      
      // 1. Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase user created:', firebaseUser.uid);

      // 2. Prepare user data
      const userData = {
        email: firebaseUser.email,
        name: name || firebaseUser.email?.split('@')[0] || 'New User',
        role: role || 'customer', // Default to customer if not provided
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // 3. Prepare customer data (if needed)
      // The phone number the customer typed at signup used to be dropped here,
      // so they had to supply it again later. Persist it with the customer doc.
      const customerData = role === 'customer' ? {
        name: name,
        email: email,
        phone: phone || '',
        userId: firebaseUser.uid,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      } : null;

      // 4. Create documents in parallel for better performance
      const promises = [
        setDoc(doc(db, 'users', firebaseUser.uid), userData)
      ];

      if (customerData) {
        promises.push(setDoc(doc(db, 'customers', firebaseUser.uid), customerData));
      }

      // Execute all database operations in parallel
      await Promise.all(promises);
      
      console.log('✅ All documents created successfully');
      console.log('🎉 Registration process completed successfully');
    } catch (error) {
      console.error("❌ Registration failed:", error);
      throw error;
    }
  }

  const logout = async () => {
    try {
      await signOut(auth)
    } catch (error) {
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (error) {
      console.error("Password reset failed:", error)
      throw error
    }
  }

  const value = {
    user,
    firebaseUser,
    loading,
    role,
    login,
    register,
    logout,
    resetPassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 